import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { useAuth } from '../contexts/AuthContext';
import { usePlatformMetrics } from '../hooks/usePlatformMetrics';
import { useAllCustomersWithSignals, formatHealthScore } from '../hooks/useAllCustomersWithSignals';
import { useAllTickets } from '../hooks/useAllTickets';
import { useFieldTrips } from '../hooks/useFieldTrips';
import { useAuditLog } from '../hooks/useAuditLog';
import {
  formatMrrCompact,
  formatCurrency,
  formatSolutionType,
  formatSlaTier,
  formatAuditAction,
  timeAgo,
  daysUntil,
} from '../lib/formatters';

export function DashboardPage() {
  const { role } = useAuth();
  const { data: metrics, isLoading: metricsLoading, isError: metricsError } = usePlatformMetrics();
  const { data: customers } = useAllCustomersWithSignals();
  const { data: allTickets } = useAllTickets({ status: ['open', 'in_progress', 'waiting_on_customer'] });
  const { data: trips } = useFieldTrips({ status: ['scheduled', 'in_progress'] });
  const isSuperadmin = role === 'superadmin';
  const { data: auditData } = useAuditLog({ pageSize: 10 }, { enabled: isSuperadmin });
  const navigate = useNavigate();

  const attentionItems = useMemo(() => {
    const items: Array<{ type: string; label: string; detail: string; urgency: 'critical' | 'warning'; href: string }> = [];

    for (const c of customers ?? []) {
      if (c.health.signals.status === 'critical') {
        items.push({
          type: 'health',
          label: c.name,
          detail: `Health ${formatHealthScore(c.health.signals.score)} · critical`,
          urgency: 'critical',
          href: `/customers/${c.id}`,
        });
      }
    }

    for (const t of allTickets ?? []) {
      if (t.sla_deadline) {
        const hoursLeft = (new Date(t.sla_deadline).getTime() - Date.now()) / (1000 * 60 * 60);
        if (hoursLeft < 4) {
          items.push({
            type: 'sla',
            label: `${t.customer?.name ?? 'Unknown customer'} — ${t.ticket_number}`,
            detail: hoursLeft < 0 ? `SLA breached ${Math.round(-hoursLeft)}h ago` : `SLA breach in ${Math.max(0, Math.round(hoursLeft))}h`,
            urgency: hoursLeft < 0 ? 'critical' : 'warning',
            href: `/tickets/${t.id}`,
          });
        }
      }
    }

    for (const c of customers ?? []) {
      const days = daysUntil(c.contract_renewal_date);
      if (days !== null && days <= 30 && days > 0) {
        items.push({ type: 'renewal', label: c.name, detail: `Renewal in ${days} days`, urgency: days <= 7 ? 'critical' : 'warning', href: `/customers/${c.id}` });
      }
    }

    return items;
  }, [customers, allTickets]);

  const deploymentBars = useMemo(() => {
    if (!metrics?.deploymentsBySolutionType) return [];
    const entries = Object.entries(metrics.deploymentsBySolutionType).sort((a, b) => b[1] - a[1]);
    const max = entries[0]?.[1] ?? 1;
    return entries.map(([type, count]) => ({ type, count, pct: (count / max) * 100 }));
  }, [metrics]);

  const mrrTiers = useMemo(() => {
    if (!metrics?.mrrBySlaTier) return [];
    return ['premium', 'enterprise', 'standard']
      .filter((t) => metrics.mrrBySlaTier[t])
      .map((tier) => ({ tier, ...metrics.mrrBySlaTier[tier] }));
  }, [metrics]);

  if (metricsLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading dashboard</span>
      </div>
    );
  }

  if (metricsError || !metrics) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6 text-sm text-notification" role="alert">
        Could not load platform metrics. Refresh to try again.
      </div>
    );
  }

  const unifi = metrics.unifi;
  const unifiConnected = unifi.sites > 0;
  const devicesKnown = unifi.devicesOnline !== null || unifi.devicesOffline !== null;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-text">Operations</h1>
          <p className="text-sm text-text-muted mt-0.5">Live counts from PazPortal, UniFi Site Manager and Auvik. Alerts stream on Command Center.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/command-center')}
          className="self-start text-sm font-medium text-primary hover:underline min-h-[44px] sm:min-h-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
        >
          Open Command Center
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          label="Active customers"
          value={String(metrics.activeCustomers)}
          hint={metrics.totalCustomers !== metrics.activeCustomers ? `${metrics.totalCustomers} total` : undefined}
          onClick={() => navigate('/customers')}
        />
        <MetricCard label="Sites" value={String(metrics.totalLocations)} onClick={() => navigate('/locations')} />
        <MetricCard
          label="UniFi sites"
          value={unifiConnected ? `${unifi.live} live` : '—'}
          hint={
            unifiConnected
              ? `${unifi.stale} stale · ${unifi.offline} offline · ${unifi.mappedSites}/${unifi.sites} mapped`
              : 'UniFi not connected'
          }
          alert={unifiConnected && unifi.live === 0}
          onClick={() => navigate('/integrations')}
        />
        <MetricCard
          label="Devices online / offline"
          value={devicesKnown ? `${unifi.devicesOnline ?? 0} / ${unifi.devicesOffline ?? 0}` : '—'}
          hint={devicesKnown ? `UniFi · ${unifi.latestAt ? timeAgo(unifi.latestAt) : 'no poll yet'}` : 'No network telemetry yet'}
          alert={(unifi.devicesOffline ?? 0) > 0}
          onClick={() => navigate('/integrations')}
        />
        <MetricCard label="Open tickets" value={String(metrics.openTickets)} onClick={() => navigate('/tickets')} />
        <MetricCard
          label="SLA breaches"
          value={String(metrics.slaBreaches)}
          hint="Open tickets past deadline"
          alert={metrics.slaBreaches > 0}
          onClick={() => navigate('/tickets')}
        />
        <MetricCard
          label="Open network alerts"
          value={metrics.auvik.openAlerts === null ? '—' : String(metrics.auvik.openAlerts)}
          hint={metrics.auvik.openAlerts === null ? 'Auvik not connected' : `Auvik · ${metrics.auvik.mappedTenants} mapped tenant${metrics.auvik.mappedTenants === 1 ? '' : 's'}`}
          alert={(metrics.auvik.openAlerts ?? 0) > 0}
          onClick={() => navigate('/command-center')}
        />
        <MetricCard label="Total MRR" value={formatMrrCompact(metrics.totalMrrCents)} onClick={() => navigate('/revenue')} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-surface rounded-lg border border-border p-4">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Attention Queue</h2>
          {attentionItems.length === 0 ? (
            <p className="text-sm text-text-muted">Nothing needs attention: no critical health scores, SLA breaches or renewals within 30 days.</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {attentionItems.map((item, i) => (
                <button
                  key={`${item.href}-${i}`}
                  type="button"
                  onClick={() => navigate(item.href)}
                  className="w-full flex items-center justify-between gap-3 p-2.5 min-h-[44px] rounded-md bg-surface-2 hover:bg-surface-strong transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text truncate">{item.label}</p>
                    <p className="text-xs text-text-muted">{item.detail}</p>
                  </div>
                  <StatusBadge status={item.urgency === 'critical' ? 'critical' : 'at-risk'} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-surface rounded-lg border border-border p-4">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">MRR by SLA Tier</h2>
          <div className="space-y-3">
            {mrrTiers.map(({ tier, sum, count }) => (
              <div key={tier} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text">{formatSlaTier(tier)}</p>
                  <p className="text-xs text-text-muted">{count} customer{count === 1 ? '' : 's'}</p>
                </div>
                <p className="text-sm font-semibold text-text">{formatCurrency(sum)}</p>
              </div>
            ))}
            {mrrTiers.length === 0 && <p className="text-sm text-text-muted">No customers with an SLA tier yet.</p>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-surface rounded-lg border border-border p-4">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Deployments by Solution</h2>
          <div className="space-y-2.5">
            {deploymentBars.map(({ type, count, pct }) => (
              <div key={type}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-text-muted">{formatSolutionType(type)}</span>
                  <span className="text-xs font-semibold text-text">{count}</span>
                </div>
                <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden" aria-hidden="true">
                  <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            ))}
            {deploymentBars.length === 0 && (
              <p className="text-sm text-text-muted">No deployments recorded. Accepted estimates and onboarding create deployment rows.</p>
            )}
          </div>
        </div>

        <div className="bg-surface rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Active Field Trips</h2>
            <button type="button" onClick={() => navigate('/trips')} className="text-xs font-medium text-primary hover:underline">All trips</button>
          </div>
          {(trips ?? []).length === 0 ? (
            <EmptyState
              icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3M3 11h18M5 7h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2z" /></svg>}
              title="No active trips"
              subtitle="Scheduled and in-progress trips will show here."
              action={{ label: 'View trips', onClick: () => navigate('/trips') }}
            />
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {(trips ?? []).map((trip) => (
                <button
                  key={trip.id}
                  type="button"
                  onClick={() => navigate(`/trips?trip=${trip.id}`)}
                  className="w-full text-left p-2.5 min-h-[44px] rounded-md bg-surface-2 hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-text">{trip.project_number}</span>
                    <StatusBadge status={trip.status ?? 'scheduled'} />
                  </div>
                  <p className="text-xs text-text-muted mt-0.5">
                    {trip.customer?.name ?? 'Unknown customer'} — {trip.location?.name ?? 'Unknown site'}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-surface rounded-lg border border-border p-4">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Recent Activity</h2>
        {!isSuperadmin ? (
          <p className="text-sm text-text-muted">The audit log is readable by superadmins only.</p>
        ) : (
          <div className="space-y-2">
            {(auditData?.entries ?? []).map((entry) => (
              <div key={entry.id} className="flex items-center gap-3 text-sm">
                <span className="text-xs text-text-faint shrink-0 w-16">{timeAgo(entry.created_at)}</span>
                <span className="text-text-muted">
                  <span className="font-medium text-text">{entry.actor_label}</span>{' '}
                  {formatAuditAction(entry.action)}
                </span>
              </div>
            ))}
            {(auditData?.entries ?? []).length === 0 && <p className="text-sm text-text-muted">No recent activity.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, hint, alert, onClick }: { label: string; value: string; hint?: string; alert?: boolean; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-surface rounded-lg border border-border p-3 sm:p-4 text-left hover:bg-surface-strong transition-colors min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <p className="text-[11px] sm:text-xs font-semibold text-text-muted uppercase tracking-wider">{label}</p>
      <p className={`text-xl sm:text-2xl font-semibold mt-1 ${alert ? 'text-notification' : 'text-text'}`}>{value}</p>
      {hint && <p className="text-[11px] text-text-faint mt-0.5 break-words">{hint}</p>}
    </button>
  );
}
